import React from 'react';
import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import App from './FeatureApp';
import {
  AudiStockcarsStoreServiceV1,
  defineAudiStockcarsStoreService,
} from '@oneaudi/stockcars-store-service';
import { audiDarkTheme, AudiPlatformProvider } from '@audi/audi-ui-react-v2';
import { Provider } from 'react-redux';
import {
  FootnoteReferenceContextProvider,
  FootnoteServiceContextProvider,
  initializeFeatureApp,
  ServicesContextProvider,
} from '@oneaudi/vtp-shared';
import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ServerRequestV1 } from '@feature-hub/server-request';
import I18nContextComponent from '@oneaudi/i18n-context';
import { I18NServiceV1 } from '@oneaudi/i18n-service';
import { GfaLayerManagerV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { AudiFootnoteServiceInterfaceV1 } from '@oneaudi/footnote-service';
import { Store } from 'redux';
import { ContentService } from '@oneaudi/content-service';
import { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import { defineConfigurationService } from '@oneaudi/vtp-configuration-service';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { EnvConfigServiceV1 } from '@oneaudi/audi-env-config-service';
import { HighlightsContent } from './interface/highlights-content.interface';
import { HighLightItem } from './interface/highlights.interfaces';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined;
  readonly 's2:server-request': ServerRequestV1 | undefined;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1 | undefined;
  readonly 'audi-stockcars-store-service': AudiStockcarsStoreServiceV1;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'gfa:locale-service': LocaleServiceV1;
  readonly 'audi-content-service': ContentService;
  readonly 'audi-footnote-reference-service': AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
  readonly 'audi-footnote-service': AudiFootnoteServiceInterfaceV1;
  readonly 'audi:envConfigService': EnvConfigServiceV1;
  readonly 'audi-tracking-service': TrackingServiceV2;
  readonly 'gfa:layer-manager': GfaLayerManagerV1;
}

export interface FeatureAppConfig {
  vehicleId?: string;
  contextServiceUrl?: string;
  contextId?: string;
}
export type CombinedConfig = FeatureAppConfig & HighlightsContent;
const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'audi-stockcars-store-service': '1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'gfa:locale-service': '1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'audi-content-service': '^1.0.0',
      'vtp-configuration-service': '^1.0.0',
      'audi:envConfigService': '1.0.0',
    },
    externals: {
      react: '^17.0.2 || ^18.2.0',
      'styled-components': '*',
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      's2:server-request': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-footnote-service': '1.0.0',
      'gfa:layer-manager': '1.0.0',
    },
  },

  ownFeatureServiceDefinitions: [defineAudiStockcarsStoreService(), defineConfigurationService()],

  create: ({
    featureServices,
    config,
  }: FeatureAppEnvironment<FeatureServiceDependencies, FeatureAppConfig>) => {
    const logger = featureServices['s2:logger'];
    logger?.info('Feature App created.');

    const stockcarsStoreService: AudiStockcarsStoreServiceV1 =
      featureServices['audi-stockcars-store-service'];

    const i18nService = featureServices['dbad:audi-i18n-service'];
    const localeService = featureServices['gfa:locale-service'];
    const audiContentService = featureServices['audi-content-service'] as ContentService;
    const combinedConfig: CombinedConfig = { ...config, ...mapContent(audiContentService) };
    // eslint-disable-next-line no-underscore-dangle
    const isEnvHeadless = audiContentService.getContent()?.__type === 'aem-headless' || false;

    const initialization = initializeFeatureApp<FeatureServiceDependencies, FeatureAppConfig>(
      featureServices,
      config!,
    );

    return {
      loadingPromise: initialization.loadingPromise?.featureAppInitializationPromise,
      render: () => {
        return (
          <AudiPlatformProvider theme={audiDarkTheme}>
            <Provider store={stockcarsStoreService.store.store as unknown as Store}>
              <ServicesContextProvider
                audiStockcarsStoreService={stockcarsStoreService}
                contextId={initialization.contextId!}
                featureAppConfig={combinedConfig}
                additionalServices={featureServices}
                envConfig={initialization.envConfig}
              >
                <I18nContextComponent
                  i18nData={i18nService}
                  language={localeService.language}
                  // @ts-ignore
                  featureServices={featureServices}
                  scopes={[]}
                >
                  <FootnoteServiceContextProvider
                    footnoteService={featureServices['audi-footnote-service']}
                  >
                    <FootnoteReferenceContextProvider
                      footnoteReferenceServiceScopeManager={
                        featureServices['audi-footnote-reference-service']
                      }
                    >
                      {initialization.loadingPromise?.state.initialized && (
                        <App
                          vehicleId={initialization.vehicleId}
                          vehicleRaw={initialization.vehicleRaw!}
                          renderOnOneCMS={isEnvHeadless}
                        />
                      )}
                    </FootnoteReferenceContextProvider>
                  </FootnoteServiceContextProvider>
                </I18nContextComponent>
              </ServicesContextProvider>
            </Provider>
          </AudiPlatformProvider>
        );
      },
    };
  },
};
type KeyValue<Type> = { [key: string]: Type };
export function mapContent(contentService: ContentService): HighlightsContent {
  const content = contentService.getContent();
  // eslint-disable-next-line no-underscore-dangle
  if (content?.__type === 'aem-headless') {
    const contentFields = content.fields;
    const config = {} as Partial<HighlightsContent>;
    Object.keys(contentFields).forEach((fieldKey) => {
      if (fieldKey === 'highlightItems') {
        config.highlightItems = [];
        contentFields.highlightItems.forEach((highlightItem: any) => {
          const highlightItemConfig = {} as Partial<HighLightItem>;
          const highlightItemFields = highlightItem.fields;
          Object.keys(highlightItemFields).forEach((highlightItemFieldKey) => {
            (highlightItemConfig as KeyValue<any>)[highlightItemFieldKey] = (
              highlightItemFields as KeyValue<any>
            )[highlightItemFieldKey];
          });
          config.highlightItems!.push(highlightItemConfig as HighLightItem);
        });
      } else {
        (config as KeyValue<any>)[fieldKey] = (contentFields as KeyValue<any>)[fieldKey];
      }
    });
    return config as HighlightsContent;
  }
  return content as HighlightsContent;
}
export default featureAppDefinition;
